import { mapState } from 'vuex';
import MessageBox from '@/components/common/messagebox/MessageBox.vue';

export default {
  name: 'ReportTable',
  props: {
    displayTotal: Boolean,
    isEditable: Boolean,
    reportData: Object,
    clientReport: Object,
    reportCosts: [Object, Array],
    reportBilledCosts: [Object, Array],
    client: Object,
    reportTableConfig: [Object, Array],
    taxrate: {
      type: Number,
      default: null
    },
    discount: {
      type: Number,
      default: null
    },
    categoryTitle: {
      type: String,
      default: null
    },
    hideCategoryTotals: {
      type: Boolean,
      default: false
    },
    displayMaterialInfo: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isModified: false,
      report: null,
      loading: false,
      displayRequired: false
    }
  },
  components: {
    MessageBox
  },
  computed: {
    ...mapState(['config']),
    clientOptions () {
      const options = [];
      options.push({ value: null, text: '' });
      this.client.users.forEach(el => {
        options.push({ value: el.api_id, text: el.name });
      });
      return options;
    },
    colspanCount () {
      let colCount = 0;
      if (this.report.header.cols) {
        colCount = this.report.header.cols.length;
      }
      if (this.report.header.hasSummedColumns) {
        if (this.report.header.displayCols.includes('VALUE')) {
          colCount += this.report.header.measureTypeCount;
        }
        if (this.report.header.displayCols.includes('CURRENCY')) {
          colCount += 1;
        }
        if (this.report.header.displayCols.includes('RATE')) {
          colCount += this.report.header.measureTypeCount;
        }
      }
      return colCount;
    },
    summaryColspanCount () {
      let colCount = 0;
      if (this.report.header.cols) {
        colCount = this.report.header.cols.length;
      }
      if (this.report.header.hasSummedColumns) {
        if (this.report.header.displayCols.includes('VALUE')) {
          colCount += this.report.header.measureTypeCount;
        }
        if (this.report.header.summaryCols.includes('RATE')) {
          colCount += 1;
        }
        if (this.report.header.summaryCols.includes('CURRENCY')) {
          colCount += this.report.header.measureTypeCount;
        }
      }
      return colCount;
    },
    statusChangeButtons () {
      const buttons = [];
      buttons.push({ label: 'general.save', nextStatus: null, fromStatuses: null, class: 'btn-secondary' });
      buttons.push({ label: 'report.action.fill', nextStatus: 'FILLED', fromStatuses: ['EMPTY'], class: 'btn-primary' });
      buttons.push({ label: 'report.action.fixed-review', nextStatus: 'FIXED_REW', fromStatuses: ['REJECTED'], class: 'btn-edit' });
      if (this.ApiRequest.userHasPermission('edit-reports')) {
        buttons.push({ label: 'report.action.reject', nextStatus: 'REJECTED', fromStatuses: null, class: 'btn-error' });
        buttons.push({ label: 'report.action.mark-fixed', nextStatus: 'FIXED', fromStatuses: null, class: 'btn-primary-border' });
        buttons.push({ label: 'report.action.approve', nextStatus: 'APPROVED', fromStatuses: null, class: 'btn-primary' });
      }
      return buttons;
    },
    canWriteBills () {
      return this.ApiRequest.userHasPermission('edit-reports');
    },
    hasAdvanceBillWithoutRealBill () {
      if (!this.clientReport || !this.clientReport.bills) {
        return false;
      }

      const getBillType = bill => this.$store.getters.getParamFromConfig('bill_types', bill.type_id, 'code');
      const hasAdvanceBill = this.clientReport.bills.some(bill => getBillType(bill) === 'ADVANCE');
      const hasRealBill = this.clientReport.bills.some(bill => getBillType(bill) === 'REAL');

      return hasAdvanceBill && !hasRealBill;
    },
    hasReportBillingDifference () {
      if (!this.reportCostValues) {
        return false;
      }

      const totalCost = this.reportCostValues.find(cost => ['total', 'license'].includes(cost.type));
      if (!totalCost || totalCost.billed === null || typeof totalCost.billed === 'undefined') {
        return false;
      }

      return Math.round(parseFloat(totalCost.value) * 100) !== Math.round(parseFloat(totalCost.billed) * 100);
    },
    reportParamCount () {
      let i = 1;
      for (const item of Object.values(this.report.fields)) {
        if (item.startOfGroup) {
          break;
        }
        i += 1;
      }
      return i;
    },
    displayBilled () {
      if (this.report.fields && this.report.fields.length > 0) {
        const lastField = this.report.fields[this.report.fields.length - 1];
        if (lastField.key === 'billed.cost') {
          return true;
        }
      }
      return false;
    },
    reportTotalColumnCount () {
      let i = 0;
      for (const item of Object.values(this.report.fields)) {
        if (item.startOfGroup) {
          i += 1;
        } else if (i > 0) {
          i += 1;
        }
      }
      if (this.displayBilled) {
        i -= 1;
      }
      return i;
    },
    reportCostValues () {
      const costs = [];
      let licenseCost = null;
      let licenseCostFull = null;
      let anyValueSet = false;
      let costOther = null;
      if (this.report && this.report.data && this.report.data.length > 0 &&
        Object.prototype.hasOwnProperty.call(this.report.data[this.report.data.length - 1], 'cost')) {
        licenseCost = this.report.data[this.report.data.length - 1].cost;
        if (this.report.data[this.report.data.length - 1].costOther) {
          costOther = this.report.data[this.report.data.length - 1].costOther;
        }
        licenseCostFull = (this.report.data[this.report.data.length - 1].costFull ? this.report.data[this.report.data.length - 1].costFull : 0);
        if (Object.prototype.hasOwnProperty.call(this.report.data[this.report.data.length - 1], 'total')) {
          const totals = this.report.data[this.report.data.length - 1].total;
          for (const key in totals) {
            if (totals[key] > 0) {
              anyValueSet = true;
            }
          }
        }
      } else {
        return null;
      }
      for (const [key, value] of Object.entries(this.reportCosts)) {
        let costValue = parseFloat(value);
        if (key === 'MINCOST') {
          let matchCost = licenseCost;
          if (costOther) {
            anyValueSet = true;
            matchCost += costOther;
          }
          if (anyValueSet && costValue > matchCost) {
            costValue = costValue - (licenseCost + costOther);
            licenseCost += costValue;
            if (licenseCost > licenseCostFull) {
              licenseCostFull = licenseCost;
            }
          } else {
            costValue = 0;
          }
        } else {
          licenseCost += value;
          licenseCostFull += value;
        }
        if (costValue > 0 || costValue < 0) {
          costs.push({ type: key, value: costValue, billed: (this.reportBilledCosts.costs[key] ? this.reportBilledCosts.costs[key].cost : null) });
        }
      }
      if (licenseCostFull !== null && (licenseCostFull - licenseCost) !== 0) {
        costs.push({ type: 'totalWithoutDiscount', value: licenseCostFull, billed: null });
        costs.push({ type: 'totaldiscount', value: (licenseCostFull - licenseCost), billed: null });
      }
      if (licenseCost !== null) {
        if (this.taxrate !== null && this.taxrate !== 0) {
          const totalWithTax = Math.round(licenseCost * 100 + licenseCost * this.taxrate) / 100;
          costs.push({ type: 'appliedTaxTo', typeparams: { taxrate: this.taxrate }, value: licenseCost, billed: this.reportBilledCosts.total.costWithoutTax });
          costs.push({ type: 'taxamount', typeparams: { taxrate: this.taxrate }, value: (totalWithTax - licenseCost), billed: null });
          costs.push({ type: 'total', value: totalWithTax, billed: this.reportBilledCosts.total.cost });
        } else {
          costs.push({ type: 'license', value: licenseCost, billed: this.reportBilledCosts.total.cost });
        }
      }

      return costs;
    },
    filledByPhone () {
      const el = this.client.users.find(el => el.api_id === this.clientReport.filled_by_id);
      if (el) {
        return el.phones;
      }
      return [];
    },
    responsiblePersonJob () {
      const el = this.client.users.find(el => el.api_id === this.clientReport.responsible_user_id);
      if (el && el.pivot) {
        return el.pivot.jobPosition;
      }
      return '';
    },
    multiselectCols () {
      return ['weight', 'quantity'];
    },
    measureTypeQuantity () {
      return this.$store.getters.getParamFromConfigByFilter('measure_types', 'code', 'GAB', 'name')[this.$i18n.locale];
    },
    hasMaterialCol () {
      if (this.report && this.report.fields && this.report.fields[0] && this.report.fields[0].key === 'material') {
        return true;
      }
      return false;
    },
    lastReportRowIdx () {
      if (this.report && this.report.data) {
        return this.report.data.length - 1;
      }
      return null;
    },
    reportStatusClass () {
      if (this.clientReport) {
        return this.ReportHelper.getReportStatusClass(this.clientReport)
      }
      return '';
    },
    reportMinPeriodId () {
      if (this.clientReport) {
        return this.clientReport.date_period_id;
      } else if (this.report && this.report.header && this.report.header.periods) {
        return this.report.header.periods[this.report.header.periods.length - 1];
      }
      return null;
    }
  },
  methods: {
    getMeasureTypeName (measureTypeId) {
      const el = this.$store.getters.getParamFromConfigByFilter('measure_types', 'id', measureTypeId, 'name');
      if (el) {
        return el[this.$i18n.locale]
      }
      return '';
    },
    multiaddChange (key2, coltype, itemId, param, constrain) {
      const val = parseFloat(this.report.data[key2].multiaddData[coltype][itemId][param]);
      if (constrain.max <= val) {
        this.report.data[key2].multiaddData[coltype][itemId][param] = (constrain.max - 1);
        this.$refs.msgBox.show(
          '',
          this.$t('report.multiadd.warning-density.title'),
          this.$t('report.multiadd.warning-density.message', {
            maxval: constrain.max
          })
        );
      } else if (val < 0) {
        this.report.data[key2].multiaddData[coltype][itemId][param] = -val;
      }
      this.recalculate();
    },
    setLoading (loading) {
      this.loading = loading;
    },
    setReport (data) {
      this.report = data;
      this.isModified = false;
    },
    recalculate () {
      this.isModified = true;
      this.report = this.ReportHelper.recalculateReportData(this.BasicHelper.cloneObject(this.report));
      this.$forceUpdate();
    },
    getRowValue (row, keyStr) {
      return this.BasicHelper.getObjectValueByString(row, keyStr);
    },
    getRowClassName (row) {
      if (row.isTotalRow) {
        return 'summary-row';
      }
      if (row.sumline) {
        return 'sumline-row';
      }
      return '';
    },
    getFieldClassName (field, isHeader) {
      let classname = (field.class ? field.class : '')
      if (field.key === 'material') {
        classname += ' text-left';
        if (!isHeader) {
          classname += ' sticky-col';
        }
      } else {
        classname += ' text-center';
      }
      if (field.startOfGroup) {
        classname += ' left-border';
      }
      return classname;
    },
    updateReport (newStatus, approved = 0) {
      let canSave = true;
      const self = this;
      this.report.data.forEach(function (el) {
        if (el.multiaddData) {
          Object.keys(el.multiaddData).forEach(function (typeId) {
            if (el.multiaddData[typeId] && el.multiaddData[typeId].length > 0) {
              Object.values(el.multiaddData[typeId]).forEach(function (multidata) {
                if (self.reportTableConfig.columns[typeId] && self.reportTableConfig.columns[typeId].multiadd_materials && self.reportTableConfig.columns[typeId].multiadd_materials[el.material]) {
                  Object.keys(self.reportTableConfig.columns[typeId].multiadd_materials[el.material]).forEach(function (param) {
                    if (!multidata[param]) {
                      canSave = false;
                    }
                  });
                }
                self.multiselectCols.forEach(function (param) {
                  if (!multidata[param]) {
                    canSave = false;
                  }
                });
              });
            }
          });
        }
      });
      if (!canSave) {
        this.displayRequired = true;
        this.$refs.msgBox.show(
          '',
          this.$t('report.required-warning.title'),
          this.$t('report.required-warning.message')
        );
      } else if (approved || newStatus === null) {
        if (!this.loading) {
          this.loading = true;
          const postData = this.ReportHelper.getReportPostData(this.report, newStatus);
          postData.clientReport = {
            filledBy: this.clientReport.filled_by_id,
            responsibleUser: this.clientReport.responsible_user_id
          }
          this.$emit('save', postData);
        }
      } else {
        let transKey = '';
        if (newStatus === 'FILLED' || newStatus === 'FIXED_REW') {
          transKey = 'fill-report-';
        }
        const newStatusNames = this.$store.getters.getParamFromConfigByFilter('client_report_statuses', 'code', newStatus, 'name');
        this.$refs.msgBoxApprove.show(
          newStatus,
          this.$t('report.status-change.' + transKey + 'title'),
          this.$t('report.status-change.' + transKey + 'message', { status: this.BasicHelper.getTranslation(newStatusNames) })
        );
      }
    },
    actionVisible (action) {
      if (!this.isEditable) {
        return false;
      }
      if (action.fromStatuses === null) {
        return true;
      }
      if (action.fromStatuses.includes(this.clientReport.status)) {
        return true;
      }
      return false;
    },
    focusInputById (materialIds) {
      const self = this;
      let el = null;
      materialIds.forEach(id => {
        const tmpEl = self.$refs['input-material-' + id];
        if (tmpEl) {
          el = tmpEl[0];
        }
      });
      if (el) {
        el.$el.focus();
      } else {
        const materialConfig = this.$store.getters.getDataFromConfigById('materials', materialIds[0]);
        if (materialConfig) {
          this.$refs.msgBox.show(
            '',
            this.$t('material.lookup.notinreport.title'),
            this.$t('material.lookup.notinreport.text', {
              materialName: this.BasicHelper.getTranslation(materialConfig.name),
              materialGroup: this.BasicHelper.getTranslation(this.$store.getters.getDataFromConfigById('material_groups', materialConfig.material_group_id).name)
            })
          );
        }
      }
    },
    fieldNotEnabled (material, type) {
      if (this.reportTableConfig && this.reportTableConfig.columns[type] && this.reportTableConfig.columns[type].disabled_materials &&
        this.reportTableConfig.columns[type].disabled_materials.find(val => (val == material))) {
        return true;
      }
      return false;
    },
    rowColIsMultiadd (material, type) {
      if (this.reportTableConfig && this.reportTableConfig.columns[type] && this.reportTableConfig.columns[type].multiadd_materials &&
        this.reportTableConfig.columns[type].multiadd_materials[material]) {
        return true;
      }
      return false;
    },
    rowIsMultiadd (material) {
      let res = false;
      if (this.reportTableConfig && this.reportTableConfig.columns) {
        Object.values(this.reportTableConfig.columns).forEach(el => {
          if (el.multiadd_materials && el.multiadd_materials[material]) {
            res = true;
          }
        });
      }
      return res;
    },
    addMultiaddRow (key2, coltype) {
      this.report.data[key2].multiaddData[coltype].push({ order: this.report.data[key2].multiaddData[coltype].length + 1 });
    },
    removeMultiaddRow (key2, coltype, idx) {
      this.report.data[key2].multiaddData[coltype].splice(idx, 1);
      Object.keys(this.report.data[key2].multiaddData[coltype]).forEach(idx => {
        this.report.data[key2].multiaddData[coltype][idx].order = idx + 1;
      });
      this.recalculate();
    },
    generateBill (approved = 0, forceRealBill = false) {
      if (approved) {
        this.$emit('generateBill', { recreate: 0, forceRealBill: forceRealBill });
      } else if (this.hasAdvanceBillWithoutRealBill && !this.hasReportBillingDifference) {
        this.$refs.msgBoxApproveD365RealBillGenerate.show(
          '',
          this.$t('report.generate-bill.title'),
          this.$t('report.generate-bill.manual-d365-message')
        );
      } else {
        this.$refs.msgBoxApproveBillGenerate.show(
          '',
          this.$t('report.generate-bill.title'),
          this.$t('report.generate-bill.message')
        );
      }
    },
    getRowspan (row) {
      let res = 0;
      if (row.rowspan) {
        res += parseInt(row.rowspan);
      }
      if (row.multiRowspan) {
        res += parseInt(row.multiRowspan);
      }
      if (res > 0) {
        return res;
      }
      return null;
    },
    getRowspanBasecat (row) {
      let rowspan = row.category_row_count;
      const multiaddLines = this.report.data.filter(el => el.category_parent == row.category_parent && el.multiRowspan);
      if (multiaddLines) {
        rowspan += multiaddLines.length;
      }
      return rowspan;
    },
    getBaseCateogtyStyle (categoryId) {
      const color = this.BasicHelper.getConfigValue('material_categories', 'id', categoryId, 'color', false);
      if (color) {
        return 'background-color:' + color + '!important;';
      }
      return '';
    },
    getMaterialInfo (materialId) {
      return this.ZPFormatHelper.getConfigValuePeriod('materials', 'id', materialId, 'info', true, this.reportMinPeriodId, 'tooltip');
    },
    getMaterialCategoryInfo (categoryId) {
      return this.ZPFormatHelper.getConfigValuePeriod('material_categories', 'id', categoryId, 'tooltip', true, this.reportMinPeriodId);
    }
  },
  mounted () {
    this.setReport(this.reportData);
  }
}
